import React from 'react';
import L from 'leaflet';
import { Marker, Popup, useMap } from 'react-leaflet';
import { User, ConnectionStatus } from '../../../services/userService'; // Use the updated User type
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faUserCheck, faUserClock, faUserPlus, faLink, faCheck, faHourglassHalf, faTimes } from '@fortawesome/free-solid-svg-icons';
import { checkConnectionStatus, sendConnectionRequest } from '../../../services/userService'; // Import connection functions
import { useAuth } from '../../../contexts/AuthContext';
import { getProfessionColor } from '../../../utils/mapping';

// ... (getProfessionColor, getMarkerSize functions - ensure getMarkerSize uses experience)

const getMarkerSize = (experience?: string): number => {
  const expLower = experience?.toLowerCase() || '';
  if (expLower.includes('senior') || expLower.includes('lead') || expLower.includes('principal')) return 36;
  if (expLower.includes('mid') || expLower.includes('intermediate')) return 30;
  if (expLower.includes('junior') || expLower.includes('entry')) return 24;
  return 28; // Default size
};

interface UserMarkerProps {
  user: User;
  isCurrentUser?: boolean;
  onSendMessage?: (userId: number, userName: string) => void;
  onSendWave?: (userId: number) => void;
  onConnect?: (userId: number, userName: string) => void;
  connectionStatus?: ConnectionStatus;
}

const UserMarker: React.FC<UserMarkerProps> = ({
  user,
  isCurrentUser = false,
  onSendMessage,
  onSendWave,
  onConnect,
  connectionStatus
}) => {
  // Check for top-level latitude and longitude
  if (user.latitude == null || user.longitude == null) {
    return null;
  }

  // Handle action buttons
  const handleSendMessage = () => {
    if (onSendMessage) {
      onSendMessage(user.id, user.name || '');
    }
  };

  const handleSendWave = () => {
    if (onSendWave) {
      onSendWave(user.id);
    }
  };

  const handleConnect = () => {
    if (onConnect && connectionStatus === 'not_connected') {
      onConnect(user.id, user.name || '');
    }
  };

  // Create custom marker icon
  const createMarkerIcon = () => {
    if (isCurrentUser) {
      // Current user marker with pulse effect
      return L.divIcon({
        className: 'user-location-marker',
        html: `
          <div class="position-relative">
            <div class="user-marker-dot">
              <i class="fas fa-user text-white"></i>
            </div>
          </div>
        `,
        iconSize: [40, 40],
        iconAnchor: [20, 20]
      });
    } else {
      // Other user markers based on profession
      const professionColor = getProfessionColor(user.profession);
      const markerSize = getMarkerSize(user.experience);
      
      return L.divIcon({
        className: 'profession-marker',
        html: `
          <div class="profession-icon" style="background-color: ${professionColor}; width: ${markerSize}px; height: ${markerSize}px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 0 5px rgba(0,0,0,0.3);"></div>
        `,
        iconSize: [markerSize, markerSize],
        iconAnchor: [markerSize/2, markerSize/2]
      });
    }
  };

  // Helper to render the connection button based on status
  const renderConnectButton = () => {
    switch (connectionStatus) {
      case 'connected':
        return (
          <button className="btn btn-sm btn-outline-success flex-grow-1" disabled>
            <FontAwesomeIcon icon={faCheck} className="me-1" /> Connected
          </button>
        );
      case 'pending_sent':
        return (
          <button className="btn btn-sm btn-outline-secondary flex-grow-1" disabled>
            <FontAwesomeIcon icon={faHourglassHalf} className="me-1" /> Request Sent
          </button>
        );
      case 'pending_received':
        // Option: Link to connections page or just show status
        return (
          <button className="btn btn-sm btn-outline-info flex-grow-1" disabled>
            <FontAwesomeIcon icon={faUserClock} className="me-1" /> Request Received
          </button>
          // Or: <Button variant="info" size="sm" onClick={() => navigate('/connections')}>View Request</Button>
        );
      case 'not_connected':
      default: // Default to connect button if status is undefined or unexpected
        return (
          <button className="btn btn-sm btn-success flex-grow-1" onClick={handleConnect}>
            <FontAwesomeIcon icon={faLink} className="me-1" /> Connect
          </button>
        );
    }
  };

  return (
    <Marker
      position={[user.latitude, user.longitude]}
      icon={createMarkerIcon()}
    >
      <Popup minWidth={200}>
        <div className="location-popup">
          <h6 className="mb-1">{isCurrentUser ? 'Your Location' : user.name}</h6>
          
          {!isCurrentUser && (
            <>
              {user.profession && <p className="mb-1"><strong>Profession:</strong> {user.profession}</p>}
              {user.company && <p className="mb-1"><strong>Company:</strong> {user.company}</p>}
              {user.city && <p className="mb-1"><strong>Location:</strong> {user.city}{user.country ? `, ${user.country}` : ''}</p>}
              {user.experience && <p className="mb-1"><strong>Experience:</strong> {user.experience}</p>}
              
              <div className="d-flex gap-2 mt-2" style={{ width: '100%', minWidth: '200px' }}>
                {/* Wave button - always show */}
                <button 
                  className="btn btn-sm btn-primary flex-grow-1" 
                  onClick={handleSendWave}
                >
                  <i className="fas fa-hand-paper"></i> Wave
                </button>
                
                {/* Message button - always show */}
                <button 
                  className="btn btn-sm btn-info flex-grow-1 text-white" 
                  onClick={handleSendMessage}
                >
                  <i className="fas fa-envelope"></i> Message
                </button>
                
                {/* Render the connection button dynamically */} 
                {renderConnectButton()}
              </div>
            </>
          )}
        </div>
      </Popup>
    </Marker>
  );
};

export default UserMarker;
